/**
 * 比較ダッシュボード (エリア / 路線) の集計の切り替え (docs/27 §9-3)。
 *
 * 2026-09-16 (オーナー指摘「保存時のまま / 集計し直す の 2 つが常にあるのはなぜ？表示項目を切り替えたら
 * 自動集計でよいのでは」): 手動の切替を廃止し、**集計項目・細かさを変えたら自動で今の解析結果から
 * 集計し直す**。既定は保存時の集計のままで、そのときは地点を読み込まない (保存済みのスナップショットは
 * 「そのとき報告した数字」であり、地区の全地点の解析結果を毎回読むのは重いため)。
 */
import { Button } from '@citizen-walk/shared-ui';
import { AXIS_GRANULARITY_LABELS } from './StreetSurveyAggregationPanel';
import type { AxisGranularity } from './computeAreaAxisCounts';
import type { ResolvedSurveyAxis } from './useStreetSurveyConfig';

/** グラフの表示形式 (2026-09-18 オーナー要望)。 */
export type SnapshotChartMode = 'bar' | 'donut';

export const SNAPSHOT_CHART_MODE_LABELS: Record<SnapshotChartMode, string> = {
  bar: '横棒（積み上げ）',
  donut: 'エリアごとの円',
};

export interface SnapshotAggregationControlsProps {
  axes: ResolvedSurveyAxis[];
  /** 選択中の集計項目 (null = 保存時の項目のまま)。 */
  axisKey: string | null;
  onAxisKeyChange: (key: string | null) => void;
  granularity: AxisGranularity;
  onGranularityChange: (g: AxisGranularity) => void;
  /** 各項目を評価の区分 (ポジ / ネガ / 中立) ごとの棒に分けるか (保存済みの内訳で足りるため再集計は不要)。 */
  splitByPolarity: boolean;
  onSplitByPolarityChange: (split: boolean) => void;
  /** 評価の内訳を持つ集計があるか (無ければ「評価で分ける」を出さない)。 */
  hasPolarity?: boolean;
  /** 今の解析結果で集計し直しているか (項目・細かさを変えたとき)。 */
  recomputing: boolean;
  /** 保存時の集計に戻す。 */
  onResetToSaved: () => void;
  loading?: boolean;
  missingPointCount?: number;
  /**
   * グラフの表示形式の切り替え (2026-09-18)。渡さないとトグルを出さない
   * (路線ダッシュボードなど、円グラフを用意していない画面のため)。
   */
  chartMode?: SnapshotChartMode;
  onChartModeChange?: (mode: SnapshotChartMode) => void;
  /** 円の単位 (「エリアごとの円」のラベルに使う。路線なら「路線ごとの円」)。 */
  donutLabel?: string;
}

export function SnapshotAggregationControls({
  axes,
  axisKey,
  onAxisKeyChange,
  granularity,
  onGranularityChange,
  splitByPolarity,
  onSplitByPolarityChange,
  hasPolarity = false,
  recomputing,
  onResetToSaved,
  loading = false,
  missingPointCount = 0,
  chartMode,
  onChartModeChange,
  donutLabel,
}: SnapshotAggregationControlsProps) {
  const selectClass = 'rounded border border-slate-300 bg-white px-2 py-1 text-xs';
  return (
    <div className="mb-3 grid grid-cols-1 gap-1 rounded border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-0 items-center gap-1 text-xs text-slate-600">
          集計する調査項目
          <select
            value={axisKey ?? ''}
            onChange={(e) => onAxisKeyChange(e.target.value || null)}
            className={selectClass}
            style={{ maxWidth: '100%' }}
          >
            <option value="">保存時の項目のまま</option>
            {axes.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 items-center gap-1 text-xs text-slate-600">
          集計の細かさ
          <select
            value={granularity}
            onChange={(e) => onGranularityChange(e.target.value as AxisGranularity)}
            className={selectClass}
            style={{ maxWidth: '100%' }}
          >
            {(Object.keys(AXIS_GRANULARITY_LABELS) as AxisGranularity[]).map((g) => (
              <option key={g} value={g}>
                {AXIS_GRANULARITY_LABELS[g]}
              </option>
            ))}
          </select>
        </label>
        {hasPolarity && (
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={splitByPolarity}
              onChange={(e) => onSplitByPolarityChange(e.target.checked)}
            />
            評価で分けて表示
          </label>
        )}
        {chartMode && onChartModeChange && (
          <span className="flex items-center gap-1 text-xs text-slate-600">
            グラフ
            <span className="inline-flex overflow-hidden rounded border border-slate-300">
              {(['bar', 'donut'] as SnapshotChartMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onChartModeChange(m)}
                  aria-pressed={chartMode === m}
                  className={`px-2 py-1 text-xs ${
                    chartMode === m ? 'bg-primary-600 text-white' : 'bg-white text-slate-600'
                  }`}
                >
                  {m === 'donut'
                    ? (donutLabel ?? SNAPSHOT_CHART_MODE_LABELS.donut)
                    : SNAPSHOT_CHART_MODE_LABELS[m]}
                </button>
              ))}
            </span>
          </span>
        )}
        {recomputing && (
          <Button size="sm" variant="secondary" onClick={onResetToSaved}>
            保存時の集計に戻す
          </Button>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        {recomputing
          ? `今の解析結果から集計し直して表示しています（保存済みの値は変わりません）。${
              loading ? '読込中…' : ''
            }${missingPointCount > 0 ? `${missingPointCount} 地点は見つかりませんでした。` : ''}`
          : '保存した時点の集計（そのとき報告した数字）で表示しています。項目や細かさを変えると、今の解析結果から集計し直します。'}
      </p>
    </div>
  );
}
